
import React, { memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Edit, Trash2 } from "lucide-react";
import { AutomationRule } from './types/automationTypes';
import { Skeleton } from "@/components/ui/skeleton";

interface AutomationRulesListProps {
  rules: AutomationRule[];
  onEdit: (rule: AutomationRule) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, enabled: boolean) => void;
  isLoading?: boolean;
}

const AutomationRuleCard = memo(({ 
  rule, 
  onEdit, 
  onDelete, 
  onToggle 
}: { 
  rule: AutomationRule;
  onEdit: (rule: AutomationRule) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, enabled: boolean) => void;
}) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'default';
    }
  };

  const getValidationStatus = () => {
    if (rule.is_valid === false) {
      return <Badge variant="destructive" className="text-xs">Invalid</Badge>;
    }
    if (rule.validation_errors && rule.validation_errors.length > 0) {
      return <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-700">Warnings</Badge>;
    }
    return <Badge variant="outline" className="text-xs border-green-500 text-green-700">Valid</Badge>;
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-sm font-medium">{rule.name}</CardTitle>
          <div className="flex items-center gap-2">
            {getValidationStatus()}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant={getPriorityColor(rule.priority)}>
            {rule.priority}
          </Badge>
          <Switch
            checked={rule.enabled}
            onCheckedChange={(enabled) => onToggle(rule.id, enabled)}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(rule)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onDelete(rule.id)}
                className="text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-2">{rule.description}</p>
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline">
            Trigger: {rule.trigger.type.replace('_', ' ')}
          </Badge>
          <Badge variant="outline">
            Actions: {rule.actions.length}
          </Badge>
          <Badge variant="outline">
            Triggered: {rule.trigger_count} times
          </Badge>
          {rule.last_triggered && (
            <Badge variant="outline">
              Last: {new Date(rule.last_triggered).toLocaleDateString()}
            </Badge>
          )}
        </div>
        {rule.validation_errors && rule.validation_errors.length > 0 && (
          <div className="mt-2 text-xs text-yellow-600">
            Issues: {rule.validation_errors.slice(0, 2).join(', ')}
            {rule.validation_errors.length > 2 && '...'}
          </div>
        )}
      </CardContent>
    </Card>
  );
});

AutomationRuleCard.displayName = 'AutomationRuleCard';

const AutomationRulesSkeleton = () => (
  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
    {[...Array(6)].map((_, i) => (
      <Card key={i} className="w-full">
        <CardHeader>
          <Skeleton className="h-4 w-3/4" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
            <div className="flex gap-2">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-5 w-20" />
            </div>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

export const AutomationRulesList: React.FC<AutomationRulesListProps> = ({
  rules,
  onEdit,
  onDelete,
  onToggle,
  isLoading = false,
}) => {
  if (isLoading) {
    return <AutomationRulesSkeleton />;
  }

  if (rules.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            <p>No automation rules found.</p>
            <p className="text-sm">Create your first rule to get started.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {rules.map((rule) => (
        <AutomationRuleCard
          key={rule.id}
          rule={rule}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
};
